/**
 * @license
 * Copyright 2021 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
import * as posedetection from "@tensorflow-models/pose-detection";
import { printError } from "../vision-algo/opencv-util";

import { Camera } from "./camera";
import { BLAZEPOSE_CONFIG, DL_MODELS, MOVENET_CONFIG, STATE } from "./params";
import { setupStats } from "./stats_panel";

import * as mpPose from "@mediapipe/pose";
import "@tensorflow/tfjs-backend-webgl";

import * as tfjsWasm from "@tensorflow/tfjs-backend-wasm";
import Calibration from "../vision-algo/calibration";
import { Gait } from "../vision-algo/gait";
import { HighlightBox } from "../vision-algo/patient_selection";
import SitStand from "../vision-algo/sitStand";
import UpperBodyArom from "../vision-algo/upperBodyAROM";
import FunctionalReach from "../vision-algo/functionalReach";
// import GestureDetector from '../vision-algo/start_gesture_detector';
import * as Sentry from "@sentry/react";
import * as tf from "@tensorflow/tfjs";
import { TestType } from "../models/Enums";
import {
  loadModelLazy,
  MODEL_KEYS,
  IDB_KEYS,
  TFHUB_MODEL_URLS,
  areModelsInCache,
} from "./model-loader";
import { TUG } from "../vision-algo/tug";
import { mCTSIB } from "../vision-algo/mCTSIB";

tfjsWasm.setWasmPaths(
  `https://cdn.jsdelivr.net/npm/@tensorflow/tfjs-backend-wasm@${tfjsWasm.version_wasm}/dist/`
);

let camera, stats;
let current_model = undefined;
let startInferenceTime,
  numInferences = 0;
let inferenceTimeSum = 0,
  lastPanelUpdate = 0;
let rafId;
let rafRun = true;
let calibration;
let gait;
let tug;
let sitStand;
let upperBodyArom;
let functionalReach;
let mctsib;
let assessment_type;

let detector = {
  model: null,
  run_pd: false,
  current_model: null,
};

// Alternate frame cropping for hand raise detection
let frameCounter = 0;
let cachedPoses = null;
// Patient highlight box (remote patient selection), smoothed across frames
const patientHighlight = new HighlightBox();
let cropCanvas = null;
let cropCtx = null;

async function createDetector(config) {
  switch (config.model) {
    case posedetection.SupportedModels.PoseNet:
      return posedetection.createDetector(config.model, {
        quantBytes: 4,
        architecture: "MobileNetV1",
        outputStride: 16,
        inputResolution: { width: 500, height: 500 },
        multiplier: 0.75,
      });
    case posedetection.SupportedModels.BlazePose:
      const runtime = config.backend.split("-")[0];
      if (runtime === "mediapipe") {
        return posedetection.createDetector(config.model, {
          runtime,
          modelType: config.modelConfig.type,
          solutionPath: `https://cdn.jsdelivr.net/npm/@mediapipe/pose@${mpPose.VERSION}`,
        });
      } else if (runtime === "tfjs") {
        // Each source is an "indexeddb://" key, a URL, or an IOHandler from
        // loadModelLazy. pose-detection only reads these two options; if either
        // is missing it silently falls back to tfhub.dev, so refuse instead.
        const { detectorModelUrl, landmarkModelUrl } = config.modelConfig;
        if (!detectorModelUrl || !landmarkModelUrl) {
          throw new Error(
            "BlazePose model sources missing; refusing tfhub default"
          );
        }
        return posedetection.createDetector(config.model, {
          runtime,
          modelType: config.modelConfig.type,
          detectorModelUrl,
          landmarkModelUrl,
        });
      }
      break;
    case posedetection.SupportedModels.MoveNet:
      let modelType;
      if (config.modelConfig.type === "lightning") {
        modelType = posedetection.movenet.modelType.SINGLEPOSE_LIGHTNING;
      } else if (config.modelConfig.type === "thunder") {
        modelType = posedetection.movenet.modelType.SINGLEPOSE_THUNDER;
      } else if (config.modelConfig.type === "multipose") {
        modelType = posedetection.movenet.modelType.MULTIPOSE_LIGHTNING;
      }
      const modelConfig = { modelType };

      // customModel is an "indexeddb://" key, a URL, or an IOHandler
      if (config.modelConfig.customModel) {
        modelConfig.modelUrl = config.modelConfig.customModel;
      }

      if (config.modelConfig.type === "multipose") {
        modelConfig.enableTracking = config.modelConfig.enableTracking;
        modelConfig.enableSmoothing = config.modelConfig.enableSmoothing;
        modelConfig.multiPoseMaxDimension =
          config.modelConfig.multiPoseMaxDimension;
        modelConfig.minPoseScore = config.modelConfig.minPoseScore;
      }
      return posedetection.createDetector(config.model, modelConfig);
    default:
      throw new Error(`Unsupported model: ${config.model}`);
  }
}

function beginEstimatePosesStats() {
  startInferenceTime = (performance || Date).now();
}

function endEstimatePosesStats() {
  const endInferenceTime = (performance || Date).now();
  inferenceTimeSum += endInferenceTime - startInferenceTime;
  ++numInferences;

  const panelUpdateMilliseconds = 1000;
  if (endInferenceTime - lastPanelUpdate >= panelUpdateMilliseconds) {
    const averageInferenceTime = inferenceTimeSum / numInferences;
    inferenceTimeSum = 0;
    numInferences = 0;
    stats.customFpsPanel.update(
      1000.0 / averageInferenceTime,
      120 /* maxValue */
    );
    // console.log("FPS: ", 1000.0 / averageInferenceTime);
    lastPanelUpdate = endInferenceTime;
  }
}

async function renderResult() {
  if (camera.video.readyState < 2) {
    await new Promise((resolve) => {
      camera.video.onloadeddata = (video) => {
        resolve(video);
      };
    });
  }

  let poses = null;
  // Detector can be null if initialization failed (for example when loading
  // from a URL that does not exist).
  // camera.drawCtx();
  if (detector.model != null && rafRun && detector.run_pd) {
    // FPS only counts the time it takes to finish estimatePoses.
    beginEstimatePosesStats();
    // Detectors can throw errors, for example when using custom URLs that
    // contain a model that doesn't provide the expected output.

    // Check if we should use cropped frames (gait assessment + MoveNet phase)
    const shouldCrop =
      assessment_type === TestType.gait &&
      gait &&
      gait.shouldUseCroppedFrames();

    try {
      if (shouldCrop) {
        frameCounter++;

        if (frameCounter % 2 === 0) {
          // Detection frame: crop -> MoveNet -> remap
          const cropRect = gait.getHandRaiseCropRect(640);

          if (cropRect) {
            // Initialize crop canvas if needed
            if (!cropCanvas) {
              cropCanvas = document.createElement("canvas");
              cropCtx = cropCanvas.getContext("2d");
            }

            // Draw cropped region to offscreen canvas
            cropCanvas.width = cropRect.cropSize;
            cropCanvas.height = cropRect.cropSize;
            cropCtx.drawImage(
              camera.video,
              cropRect.cropX,
              cropRect.cropY,
              cropRect.cropSize,
              cropRect.cropSize,
              0,
              0,
              cropRect.cropSize,
              cropRect.cropSize
            );

            // Run MoveNet on cropped frame
            poses = await detector.model.estimatePoses(cropCanvas);

            // Remap keypoints to full video space
            if (poses && poses.length > 0) {
              for (let pose of poses) {
                for (let kp of pose.keypoints) {
                  kp.x += cropRect.cropX;
                  kp.y += cropRect.cropY;
                }
                // Remap bounding box: normalized relative to crop -> normalized relative to full video
                // MoveNet box values are normalized (0-1) relative to input (cropSize x cropSize)
                // get_blazepose_bbox() expects them normalized relative to full video (1080x1920)
                if (pose.box) {
                  const vw = camera.video.videoWidth;
                  const vh = camera.video.videoHeight;
                  pose.box.xMin = (pose.box.xMin * cropRect.cropSize + cropRect.cropX) / vw;
                  pose.box.yMin = (pose.box.yMin * cropRect.cropSize + cropRect.cropY) / vh;
                  pose.box.xMax = (pose.box.xMax * cropRect.cropSize + cropRect.cropX) / vw;
                  pose.box.yMax = (pose.box.yMax * cropRect.cropSize + cropRect.cropY) / vh;
                }
              }
            }

            // Cache poses for next frame (display-only frame)
            cachedPoses = poses;
          } else {
            // Fallback if crop rect not available
            poses = await detector.model.estimatePoses(camera.video);
            cachedPoses = poses;
          }
        } else {
          // Display frame: use cached poses from previous detection frame
          poses = cachedPoses;
        }
      } else {
        // Normal flow: full frame to MoveNet
        poses = await detector.model.estimatePoses(camera.video);
      }
    } catch (error) {
      if (detector.model) {
        // detector.dispose();
        detector.model = null;
      }
      current_model = null;
      printError(error);
    }

    endEstimatePosesStats();
  }

  camera.drawCtx();

  if (poses && poses.length > 0 && current_model && !STATE.isModelChanged) {
    // Get patient ID to draw green skeleton for patient
    let patientId = null;
    const activeAssessment = getActiveAssessment();
    if (activeAssessment) {
      if (current_model === posedetection.SupportedModels.BlazePose) {
        // BlazePose is single-pose, so the first (and only) pose is always the patient
        patientId = 0;
      } else {
        // MoveNet multi-pose: use the tracked patient ID
        patientId = activeAssessment.patient_id;
      }
    }
    camera.drawResults(poses, current_model, patientId);
  }

  switch (assessment_type) {
    case TestType.five_x_sit_stand:
      sitStand.update(poses);
      break;
    case TestType.gait:
      gait.update(poses);
      break;
    case TestType.tug:
      tug.update(poses);
      break;
    case TestType.upper_body_arom:
      upperBodyArom.update(poses);
      break;
    case TestType.reach:
      functionalReach.update(poses);
      break;
    case TestType.ctsib:
      mctsib.update(poses);
      break;
    default:
      break;
  }

  drawPatientHighlight();
}

// Highlights the participant the therapist selected (remote UP/DOWN) while the selection can still
// be changed. Drawn after the assessment's update, so it shows this frame's pose rather than the last.
function drawPatientHighlight() {
  const assessment = getActiveAssessment();
  if (
    !current_model ||
    current_model === posedetection.SupportedModels.BlazePose ||
    !assessment?.is_patient_selection_open?.() ||
    !assessment.patient_selection?.has_selection()
  ) {
    patientHighlight.reset();
    return;
  }
  const patient = assessment.patient_selection.selected_patient();
  const rect = patientHighlight.update(
    patient ? camera.patientHighlightRect(patient) : null,
    patient ? patient.id : null
  );
  if (rect) camera.drawPatientHighlight(rect);
}

async function renderPrediction() {
  if (rafRun) {
    await renderResult();
  }
  if (!document.hidden && rafRun) {
    rafId = requestAnimationFrame(renderPrediction);
  }
}

// Instance of the assessment that app() last set up, or undefined if none.
export function getActiveAssessment() {
  return {
    [TestType.reach]: functionalReach,
    [TestType.five_x_sit_stand]: sitStand,
    [TestType.gait]: gait,
    [TestType.tug]: tug,
    [TestType.upper_body_arom]: upperBodyArom,
    [TestType.ctsib]: mctsib,
  }[assessment_type];
}

function reset_movenet_pose_detector() {
  detector.run_pd = false;
}

export async function closeApp() {
  cancelAnimationFrame(rafId);
  console.log("stop animation frame called");
  rafRun = false;
  
  // TESTING: Stop pose detection first to prevent new frames
  detector.run_pd = false;
  
  // TESTING: Defer model disposal to avoid WebGL backend errors
  // Let any in-flight frames complete before disposing
  setTimeout(() => {
    if (detector.model) {
      console.log("TESTING: Disposing detector model to free WebGL context");
      if (detector.model.dispose) {
        try {
          detector.model.dispose();
        } catch (error) {
          console.log("TESTING: Model disposal error (expected):", error.message);
        }
      }
    }
  }, 200); // Wait 200ms for frames to complete
  
  current_model = null;
  detector.model = null;

  if (camera && camera.video) {
    camera.clearCtx();
    camera.video.srcObject?.getTracks().forEach((track) => track.stop());
    camera.video.src = "";
  }
  console.log("stop animation frame called Completed");
}

export async function load_DL_models() {
  // Ensure TensorFlow backend is ready before loading models
  await tf.ready();
  console.log("Loading DL models with lazy loading approach");

  // Check cache status first
  const cacheStatus = await areModelsInCache();
  console.log("Model cache status:", cacheStatus);

  if (!cacheStatus.allCached) {
    console.log(
      "Some models missing from cache, will request from native when needed"
    );
  }

  // Where each model comes from: an IndexedDB key or an in-memory IOHandler
  // holding the native-bridge (or fallback) weights. Kept local so the
  // in-memory buffers can be garbage-collected once the detectors exist.
  let moveNetSource, bpDetectorSource, bpLandmarkSource;
  try {
    // Load models lazily – requested from native only if not in IndexedDB
    const loadStartTime = performance.now();

    // Load all models in parallel
    [moveNetSource, bpDetectorSource, bpLandmarkSource] = await Promise.all([
      // Fallbacks if the native bridge fails: our server first, then tfhub
      loadModelLazy(MODEL_KEYS.MOVENET, IDB_KEYS.MOVENET, [
        MOVENET_CONFIG.customModel,
        TFHUB_MODEL_URLS.MOVENET,
      ]),
      loadModelLazy(MODEL_KEYS.BLAZEPOSE_DETECTOR, IDB_KEYS.BLAZEPOSE_DETECTOR, [
        "blazepose_model/detector_model/model.json",
        TFHUB_MODEL_URLS.BLAZEPOSE_DETECTOR,
      ]),
      loadModelLazy(MODEL_KEYS.BLAZEPOSE_LANDMARK, IDB_KEYS.BLAZEPOSE_LANDMARK, [
        "blazepose_model/landmark_model/model.json",
        TFHUB_MODEL_URLS.BLAZEPOSE_LANDMARK,
      ]),
    ]);

    const loadEndTime = performance.now();
    console.log(
      `All models loaded in ${(loadEndTime - loadStartTime).toFixed(2)}ms`
    );
  } catch (error) {
    console.error("Error loading models:", error);
    DL_MODELS.MessageForSentry =
      "Error in loading models with lazy approach. Error: " + error.message;
    throw error;
  }

  // Create detectors with loaded models
  const detectorStartTime = performance.now();

  performance.mark("start_movenet_load");
  DL_MODELS.MoveNet = await createDetector({
    model: posedetection.SupportedModels.MoveNet,
    camera: {
      targetFPS: 30,
      facingMode: "user",
    },
    backend: "tfjs-webgl",
    flags: {
      WEBGL_VERSION: 2,
    },
    modelConfig: { ...MOVENET_CONFIG, customModel: moveNetSource },
  });
  performance.mark("end_movenet_load");
  performance.measure("load_movenet", "start_movenet_load", "end_movenet_load");
  const movenetEndTime = performance.now();
  console.log(
    `MoveNet detector created in ${(movenetEndTime - detectorStartTime).toFixed(
      2
    )}ms`
  );

  const blazeposestart = performance.now();
  performance.mark("start_blazepose_load");
  DL_MODELS.BlazePose = await createDetector({
    model: posedetection.SupportedModels.BlazePose,
    camera: {
      targetFPS: 30,
      facingMode: "user",
    },
    backend: "tfjs-webgl",
    flags: {
      WEBGL_VERSION: 2,
      WEBGL_CPU_FORWARD: true,
      WEBGL_PACK: true,
      WEBGL_FORCE_F16_TEXTURES: true,
    },
    modelConfig: {
      ...BLAZEPOSE_CONFIG,
      detectorModelUrl: bpDetectorSource,
      landmarkModelUrl: bpLandmarkSource,
    },
  });
  performance.mark("end_blazepose_load");
  performance.measure(
    "load_blazepose",
    "start_blazepose_load",
    "end_blazepose_load"
  );
  const blazeposeend = performance.now();
  console.log(
    `BlazePose detector created in ${(blazeposeend - blazeposestart).toFixed(
      2
    )}ms`
  );

  // Warmup models
  const warmupStart = performance.now();
  const img = document.getElementById("blank_img");

  performance.mark("start_movenet_warmup");
  await DL_MODELS.MoveNet.estimatePoses(img);
  performance.mark("end_movenet_warmup");
  performance.measure(
    "warmup_movenet",
    "start_movenet_warmup",
    "end_movenet_warmup"
  );

  performance.mark("start_blazepose_warmup");
  const dummyROI = {
    xCenter: 0.5,
    yCenter: 0.5,
    width: 0.3,
    height: 0.3,
    rotation: 0,
  };
  DL_MODELS.BlazePose["regionOfInterest"] = dummyROI;
  await DL_MODELS.BlazePose.estimatePoses(img);
  performance.mark("end_blazepose_warmup");
  performance.measure(
    "warmup_blazepose",
    "start_blazepose_warmup",
    "end_blazepose_warmup"
  );

  DL_MODELS.MessageForSentry =
    "Movenet load time:" +
    performance.getEntriesByName("load_movenet")[0].duration / 1000 +
    ", Blazepose load time:" +
    performance.getEntriesByName("load_blazepose")[0].duration / 1000;
  const warmupEnd = performance.now();
  console.log(`Models warmed up in ${(warmupEnd - warmupStart).toFixed(2)}ms`);
  console.log("DL models loaded and warmed up");
}

export async function switch_to_single_pose(regionOfInterest) {
  rafRun = false;
  /*
  This is a hack to force the blaze pose model to use the region of interest from the movenet model.
  The regionOfInterest is a private variable, acts as a seed point for landmark detection.
  https://github.com/tensorflow/tfjs-models/blob/b67a747bd0e130bb0aa773a7808f52afce621a70/pose-detection/src/blazepose_tfjs/detector.ts#L140-L161
  */
  detector.model = DL_MODELS.BlazePose;
  detector.model["regionOfInterest"] = regionOfInterest;
  current_model = posedetection.SupportedModels.BlazePose;
  console.log("regionOfInterest", regionOfInterest);
  console.log("switched to single pose");
  rafRun = true;
  detector.run_pd = true;
}

export async function switch_to_multi_pose() {
  rafRun = false;
  detector.model = DL_MODELS.MoveNet;
  current_model = posedetection.SupportedModels.MoveNet;
  console.log("switched to multipose pose");
  rafRun = true;
  detector.run_pd = true;
}

function RunMovenet() {
  detector.model = DL_MODELS.MoveNet;
  current_model = posedetection.SupportedModels.MoveNet;
  rafRun = true;
  renderPrediction();
}

export async function app(assessmentType) {
  console.log("assessmentType in Index file", assessmentType);
  console.log("stats in Index file", stats);

  if (!DL_MODELS) {
    console.log("DL_MODELS not found");
    Sentry.captureMessage("DL_MODELS not found");
  }

  if (!DL_MODELS.MoveNet) {
    console.log("DL_MODELS.MoveNet not found");
    Sentry.captureMessage("DL_MODELS.MoveNet not found");
  }

  if (!DL_MODELS.BlazePose) {
    console.log("DL_MODELS.BlazePose not found");
    Sentry.captureMessage("DL_MODELS.BlazePose not found");
  }

  if (!DL_MODELS || !DL_MODELS.MoveNet || !DL_MODELS.BlazePose) {
    try {
      await load_DL_models();
    } catch (e) {
      DL_MODELS.MessageForSentry = "Error in loading models. Error: " + e;
    }
  }

  assessment_type = assessmentType;
  rafRun = true;
  stats = setupStats();

  let start_time = performance.now();
  camera = await Camera.setupCamera(STATE.camera);

  console.log(performance.now() - start_time, "ms to setup camera");

  switch (assessment_type) {
    case TestType.five_x_sit_stand:
      sitStand = new SitStand(camera);
      window.sitStandInstance = sitStand;
      detector.run_pd = true;
      break;
    case TestType.upper_body_arom:
      upperBodyArom = new UpperBodyArom(
        camera,
        switch_to_single_pose,
        reset_movenet_pose_detector,
        switch_to_multi_pose
      );
      detector.run_pd = true;
      break;
    case TestType.reach:
      calibration = new Calibration(
        camera.video.width,
        camera.video.height,
        () => {
          detector.run_pd = true;
          console.log("enabling pose detection");
        }
      );
      functionalReach = new FunctionalReach(
        calibration,
        camera,
        switch_to_single_pose,
        reset_movenet_pose_detector,
        switch_to_multi_pose
      );
      detector.run_pd = true;
      break;
    case TestType.gait:
      calibration = new Calibration(
        camera.video.width,
        camera.video.height,
        () => {
          detector.run_pd = true;
          console.log("enabling pose detection");
        }
      );
      gait = new Gait(
        calibration,
        camera,
        switch_to_single_pose,
        reset_movenet_pose_detector
      );
      break;
    case TestType.tug:
      calibration = new Calibration(
        camera.video.width,
        camera.video.height,
        () => {
          detector.run_pd = true;
          console.log("enabling pose detection");
        }
      );
      tug = new TUG(
        calibration,
        camera,
        switch_to_single_pose,
        reset_movenet_pose_detector
      );
      break;
    case TestType.ctsib:
      calibration = new Calibration(
        camera.video.width,
        camera.video.height,
        () => {
          detector.run_pd = true;
          console.log("enabling pose detection");
        }
      );
      mctsib = new mCTSIB(
        calibration,
        camera,
        switch_to_single_pose,
        reset_movenet_pose_detector,
        switch_to_multi_pose
      );
      window.mctsibInstance = mctsib;
      // detector.run_pd = true;
      break;
    default:
      console.log("%c Invalid assessment type", "color: red");
      break;
  }
  RunMovenet();
}
